import type { TenantInfo } from '@/store/authStore'

/**
 * Construye la URL para cambiar a otro tenant en su subdominio.
 *
 * Devuelve `null` si el destino es el MISMO host (no hay que navegar: basta
 * `setActiveTenant` + ir al dashboard dentro del SPA).
 *
 * El SSO cross-subdominio se apoya en las cookies `zen_session` /
 * `zen_refresh_token` scopeadas al dominio padre (`.clobi.io`): al llegar al
 * subdominio destino el `HydrationGate` lee la sesión y renueva el access token
 * sin re-login. El `?t=` le indica qué tenant activar. (En `localhost` el
 * navegador NO comparte cookies entre subdominios, así que el SSO solo funciona
 * en un dominio real.)
 *
 * Preserva el puerto actual — clave en dev (`:5173`); en prod el puerto es vacío.
 */
export function buildTenantSwitchUrl(target: TenantInfo, tenants: TenantInfo[]): string | null {
  const { protocol, hostname, port } = window.location
  const portPart = port ? `:${port}` : ''
  const targetPrefix = target.schema.replace(/_/g, '-')
  const query = `?t=${target.schema}${_sidebarStateParam()}`

  // ¿El host actual es `<prefijo>.<sufijo>`? Derivar el sufijo y reemplazar el
  // prefijo por el del tenant destino (ej. mi-empresa.clobi.io → otra.clobi.io).
  const matching = tenants.find((t) => hostname.startsWith(t.schema.replace(/_/g, '-') + '.'))
  if (matching) {
    const currentPrefix = matching.schema.replace(/_/g, '-')
    const suffix = hostname.slice(currentPrefix.length) // ej. '.clobi.io'
    const targetHost = targetPrefix + suffix
    if (targetHost === hostname) return null
    return `${protocol}//${targetHost}${portPart}/dashboard${query}`
  }

  // Fallback: dominio explícito del tenant (producción con dominio propio).
  if (target.domain && target.domain !== hostname) {
    return `${protocol}//${target.domain}${portPart}/dashboard${query}`
  }

  return null
}

/**
 * `&sb=0|1` con el estado del sidebar para preservarlo cross-subdominio.
 * `localStorage` es por-origen, así que sin esto el subdominio destino mostraría
 * SU propia preferencia (a veces expandido aunque el origen estuviera colapsado).
 * El `HydrationGate` lo aplica al llegar. Clave alineada con AppLayout (`zen-sidebar-open`).
 */
function _sidebarStateParam(): string {
  try {
    const sb = localStorage.getItem('zen-sidebar-open')
    if (sb === '0' || sb === '1') return `&sb=${sb}`
  } catch {
    /* localStorage no disponible */
  }
  return ''
}

/**
 * Qué tenant dice el HOST, cuando difiere del que el store trae guardado.
 *
 * `localStorage` es POR ORIGEN, y el switcher escribía el tenant destino ANTES de
 * navegar — o sea en el origen que estaba abandonando. Volver después a ese
 * subdominio hidrataba con el tenant equivocado: el usuario entraba a
 * `zenlatam.clobi.io` y el selector decía «Marva». Peor que cosmético: con el
 * tenant activo mal, `activeTenant.country` gobierna locale, moneda y el rótulo
 * del identificador tributario en seis lugares de `lib/locale.ts`.
 *
 * El HOST manda: es lo que resuelve el schema en el backend, así que es la única
 * fuente que no puede estar desincronizada. Devuelve `null` si ya coinciden o si
 * el host no corresponde a ningún tenant conocido (dominio propio, `app.*`).
 */
export function tenantSegunHost(
  tenants: TenantInfo[],
  activeSchema: string | null,
): string | null {
  const { hostname } = window.location
  const delHost = tenants.find(
    (t) => hostname.startsWith(t.schema.replace(/_/g, '-') + '.') || t.domain === hostname,
  )
  if (!delHost || delHost.schema === activeSchema) return null
  return delHost.schema
}
